import { useCallback, useEffect } from "react";

import { create } from "zustand";

import { useAuth } from "@/contexts/AuthContext";
import { useActiveWorkspaceId } from "@/contexts/workspace-context";
import { billingApi } from "@/lib/api/billing";

/**
 * Payment-method handover state, shared app-wide.
 *
 * After a paid-workspace ownership transfer, GET /billing/subscription returns
 * `pendingPmHandover: true` for the new owner until they either add their own
 * card or explicitly keep the previous owner's card. Both the shell attention
 * bar (pm-handover-bar) and the billing-page banner (pm-handover-banner) read
 * this one store, so resolving in either place clears the other instantly.
 *
 * The flag is a PER-WORKSPACE fact, so the store keys everything by
 * workspaceId. A user can own many workspaces and only one may be mid-handover;
 * reading by the active workspace id means one workspace's banner can never
 * leak onto another when you switch between them.
 */

const REFRESH_THROTTLE_MS = 30_000;

interface PmHandoverStoreState {
  /** pending flag per workspaceId; absent = unknown, treated as not pending */
  pendingByWorkspace: Record<string, boolean>;
  /** epoch ms of the last started fetch per workspaceId — throttles refreshes */
  lastFetchedAt: Record<string, number>;
  setPending: (workspaceId: string, pending: boolean) => void;
  markFetched: (workspaceId: string) => void;
  /** Optimistically clear the flag for one workspace (after dismiss/add-card) */
  resolveLocally: (workspaceId: string) => void;
}

export const usePmHandoverStore = create<PmHandoverStoreState>((set) => ({
  pendingByWorkspace: {},
  lastFetchedAt: {},
  setPending: (workspaceId, pending) =>
    set((state) => ({
      pendingByWorkspace: {
        ...state.pendingByWorkspace,
        [workspaceId]: pending,
      },
    })),
  markFetched: (workspaceId) =>
    set((state) => ({
      lastFetchedAt: { ...state.lastFetchedAt, [workspaceId]: Date.now() },
    })),
  resolveLocally: (workspaceId) =>
    set((state) => ({
      pendingByWorkspace: {
        ...state.pendingByWorkspace,
        [workspaceId]: false,
      },
    })),
}));

interface UsePmHandoverResult {
  pending: boolean;
  /** Re-fetch the subscription. Throttled (30s) unless `force` is true. */
  refresh: (force?: boolean) => Promise<void>;
  resolveLocally: () => void;
}

/**
 * Fetches GET /billing/subscription for the ACTIVE workspace on mount, on
 * window focus, and whenever the active workspace changes (throttled to at most
 * once per 30s per workspace) and exposes that workspace's pending flag. Safe
 * to mount from multiple components.
 */
export function usePmHandover(): UsePmHandoverResult {
  const { getIdToken } = useAuth();
  const workspaceId = useActiveWorkspaceId();

  const pending = usePmHandoverStore((s) =>
    workspaceId ? s.pendingByWorkspace[workspaceId] ?? false : false,
  );

  const resolveLocally = useCallback((): void => {
    if (!workspaceId) return;
    usePmHandoverStore.getState().resolveLocally(workspaceId);
  }, [workspaceId]);

  const refresh = useCallback(
    async (force = false): Promise<void> => {
      if (!workspaceId) return;
      const { lastFetchedAt, markFetched, setPending } =
        usePmHandoverStore.getState();
      if (
        !force &&
        Date.now() - (lastFetchedAt[workspaceId] ?? 0) < REFRESH_THROTTLE_MS
      ) {
        return;
      }
      markFetched(workspaceId);
      try {
        const token = await getIdToken();
        if (!token) return;
        // getSubscription resolves the workspace from the X-Workspace-Id header,
        // which tracks the same active workspace id, so this result belongs to
        // `workspaceId` — tag it as such in the store.
        const subscription = await billingApi.getSubscription(token);
        setPending(workspaceId, Boolean(subscription.pendingPmHandover));
      } catch (error) {
        // Non-critical — keep the last known state, retry on next focus
        console.error("Failed to refresh pm-handover state:", error);
      }
    },
    [getIdToken, workspaceId],
  );

  useEffect(() => {
    if (!workspaceId) return;
    void refresh();
    const handleFocus = (): void => {
      void refresh();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [workspaceId, refresh]);

  return { pending, refresh, resolveLocally };
}
